import React from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import App from 'App';

import AuthGuardRoute from 'components/common/AuthGuardRoute';
import MainLayout from 'layouts/MainLayout';
import ErrorLayout from 'layouts/ErrorLayout';

import Dashboard from 'components/dashboards/default';

// Login
import Login from 'components/Login/Login';

// My Page
import MyPage from 'components/my-page/MyPage';

// Password Reset
import PasswordReset from 'components/password-reset/PasswordReset';
import ConfirmMail from 'components/password-reset/ConfirmMail';
import PasswordResetConfirm from 'components/password-reset/PasswordResetConfirm';

// Product
import ChannelProducts from 'components/add_product/channel-product/ChannelProducts';
import ManageProduct from 'components/add_product/manage-product/ManageProduct';
import ManageProductDetail from 'components/add_product/manage-product/ManageProductDetail';

// Purchase Order
import PurchaseOrders from 'components/purchase-order/purchase-order/PurchaseOrders';
import PurchaseOrderCreate from 'components/purchase-order/purchase-order/PurchaseOrderCreate';
import PurchaseOrderDetails from 'components/purchase-order/purchase-order/PurchaseOrderDetails';
import Vendors from 'components/purchase-order/vendor/Vendors';
import VendorCreate from 'components/purchase-order/vendor/VendorCreate';
import VendorDetails from 'components/purchase-order/vendor/VendorDetails';
import Grn from 'components/purchase-order/grn/Grn';
import GrnDetails from 'components/purchase-order/grn/GrnDetails';
import Putaway from 'components/purchase-order/put-away/Putaway';
import PutawayDetails from 'components/purchase-order/put-away/PutawayDetails';
import CreatePutaway from 'components/purchase-order/put-away/CreatePutaway';

// Order
import Orders from 'components/add_order/order/Orders';
import OrderDetail from 'components/add_order/order/OrderDetail/index';
import Shipment from 'components/add_order/shipment/Shipment';
import Picklist from 'components/add_order/picklist/Picklist';
import PicklistDetail from 'components/add_order/picklist/PicklistDetail/index';
import CreatePicklist from 'components/add_order/picklist/CreatePicklist';
import ProcessPicklist from 'components/add_order/picklist/ProcessPicklist/ProcessPicklist';
import Manifests from 'components/add_order/manifest/Manifests';
import ManifestDetail from 'components/add_order/manifest/ManifestDetail';

// Inventory
import Inventory from 'components/add_inventory/Inventory';
import InventoryHistory from 'components/add_inventory/inventory-history/InventoryHistory';

// Return Manifest
import ReturnManifest from 'components/add_return/return-manifest/ReturnManifests';
import ReturnManifestDetail from 'components/add_return/return-manifest/ReturnManifestDetail';

// Bulk Action
import BulkAction from 'components/bulk-action/BulkAction';

// Admin
import User from 'components/admin/user/User';
import UserDetail from 'components/admin/user/UserDetail';
import Shelf from 'components/admin/shelf/Shelf';
import ShelfDetail from 'components/admin/shelf/ShelfDetail';
import Warehouse from 'components/admin/warehouse/Warehouse';
import WarehouseDetail from 'components/admin/warehouse/WarehouseDetail';
import Sequence from 'components/admin/sequence/Sequence';
import ShippingProvider from 'components/admin/shipping-provider/ShippingProvider';

// Error
import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';

import paths, { rootPaths } from 'routes/paths';

const routes = [
  {
    element: <App />,
    children: [
      {
        path: paths.login,
        element: <Login />,
      },
      {
        path: paths.passwordReset,
        element: <PasswordReset />,
      },
      {
        path: paths.confirmMail,
        element: <ConfirmMail />,
      },
      {
        path: paths.passwordResetConfirm,
        element: <PasswordResetConfirm />,
      },
      {
        path: rootPaths.errorsRoot,
        element: <ErrorLayout />,
        children: [
          {
            path: paths.error404,
            element: <Error404 />,
          },
          {
            path: paths.error500,
            element: <Error500 />,
          },
        ],
      },
      {
        path: '/',
        element: <AuthGuardRoute element={<MainLayout />} />,
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: paths.myPage,
            element: <MyPage />,
          },
          {
            path: rootPaths.productRoot,
            children: [
              {
                index: true,
                element: <ChannelProducts />,
              },
              {
                path: paths.manageProduct,
                element: <ManageProduct />,
              },
              {
                path: paths.productDetails(':productId'),
                element: <ManageProductDetail />,
              },
            ],
          },
          {
            path: rootPaths.purchaseOrderRoot,
            children: [
              {
                index: true,
                element: <PurchaseOrders />,
              },
              {
                path: paths.purchaseOrderCreate,
                element: <PurchaseOrderCreate />,
              },
              {
                path: paths.purchaseOrderDetails(':purchaseOrderId'),
                element: <PurchaseOrderDetails />,
              },
              {
                path: paths.vendor,
                element: <Vendors />,
              },
              {
                path: paths.vendorCreate,
                element: <VendorCreate />,
              },
              {
                path: paths.vendorDetails(':vendorId'),
                element: <VendorDetails />,
              },
              {
                path: paths.grn,
                element: <Grn />,
              },
              {
                path: paths.grnDetails(':grnId'),
                element: <GrnDetails />,
              },
              {
                path: paths.putAway,
                element: <Putaway />,
              },
              {
                path: paths.putAwayDetails(':putawayId'),
                element: <PutawayDetails />,
              },
              {
                path: paths.putAwayCreate,
                element: <CreatePutaway />,
              },
            ],
          },
          {
            path: rootPaths.orderRoot,
            children: [
              {
                index: true,
                element: <Orders />,
              },
              {
                path: paths.orderDetails(':orderId'),
                element: <OrderDetail />,
              },
              {
                path: paths.shipment,
                element: <Shipment />,
              },
              {
                path: paths.picklist,
                element: <Picklist />,
              },
              {
                path: paths.picklistDetails(':picklistId'),
                element: <PicklistDetail />,
              },
              {
                path: paths.picklistCreate,
                element: <CreatePicklist />,
              },
              {
                path: paths.picklistProcess(':picklistId'),
                element: <ProcessPicklist />,
              },
              {
                path: paths.manifests,
                element: <Manifests />,
              },
              {
                path: paths.manifestsDetails(':manifestId'),
                element: <ManifestDetail />,
              },
            ],
          },
          {
            path: rootPaths.inventoryRoot,
            children: [
              {
                index: true,
                element: <Inventory />,
              },
              {
                path: paths.inventoryHistory,
                element: <InventoryHistory />,
              },
            ],
          },
          {
            path: rootPaths.returnManifestRoot,
            children: [
              {
                index: true,
                element: <ReturnManifest />,
              },
              {
                path: paths.returnManifestsDetails(':returnManifestId'),
                element: <ReturnManifestDetail />,
              },
            ],
          },
          {
            path: rootPaths.bulkActionRoot,
            element: <BulkAction />,
          },
          {
            path: rootPaths.adminRoot,
            children: [
              {
                path: paths.user,
                element: <User />,
              },
              {
                path: paths.userDetails(':userId'),
                element: <UserDetail />,
              },
              {
                path: paths.shelf,
                element: <Shelf />,
              },
              {
                path: paths.shelfDetails(':shelfId'),
                element: <ShelfDetail />,
              },
              {
                path: paths.warehouse,
                element: <Warehouse />,
              },
              {
                path: paths.warehouseDetails(':warehouseId'),
                element: <WarehouseDetail />,
              },
              {
                path: paths.sequence,
                element: <Sequence />,
              },
              {
                path: paths.shippingProvider,
                element: <ShippingProvider />,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to={paths.error404} replace />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: process.env.PUBLIC_URL,
});

export default routes;
